import { useState, useEffect, useCallback, useMemo } from 'react'
import { Menu, MenuItem } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import {
	getPackages,
	createPackage,
	updatePackage,
	deletePackage,
	createAddOn,
	updateAddOn,
	deleteAddOn,
} from '../../../features/packageSlice'
import ButtonCustom from '../../atoms/button/ButtonCustom'
import PackageModal from './modals/PackageModal'
import AddOnModal from './modals/AddOnModal'
import CommonDeleteModal from '../../atoms/modal/CommonDeleteModal'
import { toastHelper } from '../../../util/toastHelper'
import PackagesListView from './PackagesListView'

const PackagesCard = ({ cardStyle }) => {
	const dispatch = useDispatch()
	const reduxPackages = useSelector((state) => state.packages.packages)
	const vendorId = useSelector((state) => state.auth.user?._id)

	const [packageModalOpen, setPackageModalOpen] = useState(false)
	const [modal, setModal] = useState({
		name: '',
		data: null,
	})
	const [deleting, setDeleting] = useState(false)
	const [packageTabValue, setPackageTabValue] = useState(0)
	const [packageFormData, setPackageFormData] = useState({
		name: '',
		description: '',
		priceType: '',
		price: '',
		image: null,
		imagePreview: null,
		addOnsIds: [],
	})
	const [isEditingPackage, setIsEditingPackage] = useState(false)
	const [editingPackageId, setEditingPackageId] = useState(null)
	const [packageCreated, setPackageCreated] = useState(false)
	const [originalPackageData, setOriginalPackageData] = useState(null)

	const [addonModalOpen, setAddonModalOpen] = useState(false)
	const [addonFormData, setAddonFormData] = useState({
		type: '',
		name: '',
		packageIds: [],
		description: '',
		priceType: '',
		price: '',
		minQuantity: '',
		maxQuantity: '',
		isThisAddOnIsRequired: false,
		constraints: [],
		image: null,
		imagePreview: null,
	})
	const [isEditingAddon, setIsEditingAddon] = useState(false)
	const [editingAddonIndex, setEditingAddonIndex] = useState(null)
	const [editingAddonPackageIndex, setEditingAddonPackageIndex] =
		useState(null)

	const [menuAnchor, setMenuAnchor] = useState(null)
	const [selectedPackageIndex, setSelectedPackageIndex] = useState(null)
	const [isLoading, setLoading] = useState(false)
	useEffect(() => {
		if (vendorId) {
			dispatch(getPackages({ vendorId }))
		}
	}, [dispatch, vendorId])

	const packages = useMemo(() => {
		return reduxPackages || []
	}, [reduxPackages])

	const handleOpenPackageModal = (packageId = null) => {
		if (packageId !== null) {
			const pkg = packages.find((p) => p._id === packageId)
			if (pkg) {
				const initialData = {
					name: pkg.name || '',
					description: pkg.description || '',
					priceType: pkg.priceType || '',
					price: pkg.price?.toString() || '',
					image: null,
					imagePreview: pkg.image || null,
					addOnsIds: pkg.addOnsIds || [],
				}
				setPackageFormData(initialData)
				setOriginalPackageData(initialData)
				setIsEditingPackage(true)
				setEditingPackageId(packageId)
				setPackageCreated(true)
			}
		} else {
			const initialData = {
				name: '',
				description: '',
				priceType: '',
				price: '',
				image: null,
				imagePreview: null,
				addOnsIds: [],
			}
			setPackageFormData(initialData)
			setOriginalPackageData(initialData)
			setIsEditingPackage(false)
			setEditingPackageId(null)
			setPackageCreated(false)
		}
		setPackageTabValue(0)
		setPackageModalOpen(true)
	}

	const handleClosePackageModal = () => {
		setPackageModalOpen(false)
		setPackageFormData({
			name: '',
			description: '',
			priceType: '',
			price: '',
			image: null,
			imagePreview: null,
			addOnsIds: [],
		})
		setIsEditingPackage(false)
		setEditingPackageId(null)
		setPackageCreated(false)
		setOriginalPackageData(null)
	}

	const handlePackageImageChange = (file) => {
		setPackageFormData({
			...packageFormData,
			image: file,
			imagePreview: file ? URL.createObjectURL(file) : null,
		})
	}

	const handleSavePackage = async () => {
		try {
			if (
				!packageFormData.price ||
				!packageFormData.priceType ||
				!packageFormData.name?.trim() ||
				!packageFormData.description?.trim()
			)
				return toastHelper(
					'Please fill all the required fields',
					'error'
				)
			if (packageFormData.price <= 0)
				return toastHelper('Please enter valid price', 'error')
			setLoading(true)
			const packageData = {
				name: packageFormData.name?.trim(),
				description: packageFormData.description?.trim(),
				priceType: packageFormData.priceType,
				price: packageFormData.price.toString(),
				image: packageFormData.image,
			}

			if (isEditingPackage && editingPackageId) {
				const result = await dispatch(
					updatePackage({
						id: editingPackageId,
						data: packageData,
					})
				).unwrap()
				if (result?.success) {
					dispatch(getPackages({ vendorId }))
					handleClosePackageModal()
				}
			} else {
				const result = await dispatch(
					createPackage(packageData)
				).unwrap()
				if (result?.success) {
					setPackageCreated(true)
					handleClosePackageModal()
					dispatch(getPackages({ vendorId }))
				}
			}
		} catch (error) {
			console.error('Failed to save package:', error)
		} finally {
			setLoading(false)
		}
	}

	const getAddonPackageIds = (addon, fallbackPackageId) => {
		if (!addon) return fallbackPackageId ? [fallbackPackageId] : []

		if (
			Array.isArray(addon.attachedPackageId) &&
			addon.attachedPackageId.length
		) {
			if (addon.attachedPackageId?.includes(fallbackPackageId))
				return addon.attachedPackageId
					.map((p) =>
						typeof p === 'object' && p !== null ? p._id : p
					)
					.filter(Boolean)
			else
				return [...addon.attachedPackageId, fallbackPackageId]
					.map((p) =>
						typeof p === 'object' && p !== null ? p._id : p
					)
					.filter(Boolean)
		}

		if (Array.isArray(addon.packageIds) && addon.packageIds.length) {
			return addon.packageIds
		}
		if (addon.packageId) {
			return [addon.packageId]
		}

		return fallbackPackageId ? [fallbackPackageId] : []
	}

	const handleOpenAddonModal = useCallback(
		(packageIndex = null, addonIndex = null) => {
			if (addonIndex !== null && packageIndex !== null) {
				const pkg = packages[packageIndex]
				const addon = pkg?.addOnsIds?.[addonIndex]
				if (addon && typeof addon === 'object') {
					setAddonFormData({
						type: addon.type || '',
						name: addon.name || '',
						packageIds: getAddonPackageIds(addon, pkg?._id),
						description: addon.description || '',
						priceType: addon.priceType || '',
						price: addon.price?.toString() || '',
						minQuantity: addon.minQuantity?.toString() || '',
						maxQuantity: addon.maxQuantity?.toString() || '',
						isThisAddOnIsRequired:
							addon.isThisAddOnIsRequired || false,
						constraints: addon.constraints || [],
						image: null,
						imagePreview: addon.image || null,
					})
					setIsEditingAddon(true)
					setEditingAddonIndex(addonIndex)
					setEditingAddonPackageIndex(packageIndex)
				}
			} else {
				const packageId =
					packageIndex !== null
						? packages[packageIndex]?._id
						: packageModalOpen
							? editingPackageId
							: null

				setAddonFormData({
					type: '',
					name: '',
					packageIds: packageId ? [packageId] : [],
					description: '',
					priceType: '',
					price: '',
					minQuantity: '',
					maxQuantity: '',
					isThisAddOnIsRequired: false,
					constraints: [],
					image: null,
					imagePreview: null,
				})
				setIsEditingAddon(false)
				setEditingAddonIndex(null)
				setEditingAddonPackageIndex(packageIndex)
			}
			setAddonModalOpen(true)
		},
		[packages, packageModalOpen, editingPackageId]
	)

	const handleCloseAddonModal = () => {
		setAddonModalOpen(false)
		setAddonFormData({
			type: '',
			name: '',
			packageIds: [],
			description: '',
			priceType: '',
			price: '',
			minQuantity: '',
			maxQuantity: '',
			isThisAddOnIsRequired: false,
			constraints: [],
			image: null,
			imagePreview: null,
		})
		setIsEditingAddon(false)
		setEditingAddonIndex(null)
		setEditingAddonPackageIndex(null)
	}

	const handleAddonImageChange = (file) => {
		setAddonFormData({
			...addonFormData,
			image: file,
			imagePreview: file ? URL.createObjectURL(file) : null,
		})
	}

	const handleSaveAddon = async () => {
		try {
			if (
				!addonFormData.type ||
				!addonFormData.price ||
				!addonFormData.priceType ||
				!addonFormData.name?.trim() ||
				!addonFormData.description?.trim()
			)
				return toastHelper(
					'Please fill all the required fields',
					'error'
				)
			if (addonFormData.packageIds.length === 0)
				return toastHelper(
					'Please select at least one package',
					'error'
				)
			if (
				!Array.isArray(addonFormData.constraints) ||
				addonFormData.constraints.length === 0
			) {
				toastHelper('Please add at least one constraint!', 'error')
				return
			}
			const hasEmptyConstraint = addonFormData.constraints.some(
				(c) => !c?.label?.trim() || !c?.value?.trim()
			)
			if (hasEmptyConstraint) {
				toastHelper(
					'Please fill label and value for all constraints!',
					'error'
				)
				return
			}
			if (addonFormData.price <= 0) {
				toastHelper('Please enter valid price', 'error')
				return
			}
			if (
				!!addonFormData.minQuantity &&
				!!addonFormData.maxQuantity &&
				Number(addonFormData.maxQuantity) <
					Number(addonFormData.minQuantity)
			) {
				toastHelper(
					'Max Quantity should be greater than or equal to Min Quantity',
					'error'
				)
				return
			}
			setLoading(true)
			const addonData = {
				packageIds: addonFormData.packageIds,
				type: addonFormData.type,
				name: addonFormData.name?.trim(),
				description: addonFormData.description?.trim(),
				priceType: addonFormData.priceType,
				price: addonFormData.price.toString(),
				minQuantity: !!addonFormData.minQuantity
					? parseInt(addonFormData.minQuantity)
					: undefined,
				maxQuantity: !!addonFormData.maxQuantity
					? parseInt(addonFormData.maxQuantity)
					: undefined,
				isThisAddOnIsRequired: addonFormData.isThisAddOnIsRequired,
				constraints:
					addonFormData.constraints.length > 0
						? JSON.stringify(addonFormData.constraints)
						: undefined,
				image: addonFormData.image,
			}

			Object.keys(addonData).forEach((key) => {
				if (addonData[key] === undefined) {
					delete addonData[key]
				}
			})

			if (
				packageModalOpen &&
				editingAddonPackageIndex === null &&
				editingPackageId
			) {
				const shouldAttachToThisPackage =
					Array.isArray(addonData.packageIds) &&
					addonData.packageIds.includes(editingPackageId)

				const updatedAddons = [...(packageFormData.addOnsIds || [])]
				if (isEditingAddon && editingAddonIndex !== null) {
					const existingAddon = updatedAddons[editingAddonIndex]
					if (existingAddon?._id) {
						const result = await dispatch(
							updateAddOn({
								id: existingAddon._id,
								data: addonData,
							})
						).unwrap()
						if (result?.success) {
							if (result?.data) {
								if (shouldAttachToThisPackage) {
									updatedAddons[editingAddonIndex] =
										result.data
								} else {
									updatedAddons.splice(editingAddonIndex, 1)
								}
							}
							setPackageFormData({
								...packageFormData,
								addOnsIds: updatedAddons,
							})
							dispatch(getPackages({ vendorId }))
							handleCloseAddonModal()
							handleClosePackageModal()
						}
					}
				} else {
					const result = await dispatch(
						createAddOn(addonData)
					).unwrap()
					if (result?.success && result?.data) {
						if (shouldAttachToThisPackage) {
							updatedAddons.push(result.data)
							setPackageFormData({
								...packageFormData,
								addOnsIds: updatedAddons,
							})
						}
						dispatch(getPackages({ vendorId }))
						handleCloseAddonModal()
					}
				}
			} else {
				if (
					isEditingAddon &&
					editingAddonIndex !== null &&
					editingAddonPackageIndex !== null
				) {
					const pkg = packages[editingAddonPackageIndex]
					const addon = pkg?.addOnsIds?.[editingAddonIndex]
					if (addon?._id) {
						const result = await dispatch(
							updateAddOn({
								id: addon._id,
								data: addonData,
							})
						).unwrap()
						if (result?.success) {
							dispatch(getPackages({ vendorId }))
							handleCloseAddonModal()
							handleClosePackageModal()
						}
					}
				} else {
					const result = await dispatch(
						createAddOn(addonData)
					).unwrap()
					if (result?.success && result?.data) {
						dispatch(getPackages({ vendorId }))
						handleCloseAddonModal()
					}
				}
			}
		} catch (error) {
			console.error('Failed to save addon:', error)
		} finally {
			setLoading(false)
		}
	}

	const resolveAddonFromFormData = (addonOrId) => {
		const addOnsIds = packageFormData.addOnsIds || []

		if (addonOrId && typeof addonOrId === 'object') {
			const byIdIndex = addonOrId._id
				? addOnsIds.findIndex(
						(a) =>
							(a &&
								typeof a === 'object' &&
								a._id === addonOrId._id) ||
							a === addonOrId
					)
				: addOnsIds.findIndex((a) => a === addonOrId)

			return {
				addon: addonOrId,
				index: byIdIndex !== -1 ? byIdIndex : null,
			}
		}

		const id = addonOrId
		const index = addOnsIds.findIndex(
			(a) => (a && typeof a === 'object' && a._id === id) || a === id
		)

		return {
			addon: index !== -1 ? addOnsIds[index] : null,
			index: index !== -1 ? index : null,
		}
	}

	const handleEditAddonInPackage = (addonOrId) => {
		const { addon, index } = resolveAddonFromFormData(addonOrId)
		if (!addon) return

		const packageIds = getAddonPackageIds(addon, editingPackageId)

		setAddonFormData({
			type: addon.type || '',
			name: addon.name || addon || '',
			packageIds,
			description: addon.description || '',
			priceType: addon.priceType || '',
			price: addon.price?.toString() || '0',
			minQuantity: addon.minQuantity?.toString() || '',
			maxQuantity: addon.maxQuantity?.toString() || '',
			isThisAddOnIsRequired: addon.isThisAddOnIsRequired || false,
			constraints: addon.constraints || [],
			image: null,
			imagePreview: addon.image || null,
		})
		setIsEditingAddon(true)
		setEditingAddonIndex(index)
		setEditingAddonPackageIndex(null)
		setAddonModalOpen(true)
	}

	const handleMenuOpen = (event, index) => {
		event.stopPropagation()
		setMenuAnchor(event.currentTarget)
		setSelectedPackageIndex(index)
	}

	const handleMenuClose = () => {
		setMenuAnchor(null)
		setSelectedPackageIndex(null)
	}

	const handleEditPackage = () => {
		handleMenuClose()
		const pkg = packages[selectedPackageIndex]
		if (pkg?._id) {
			handleOpenPackageModal(pkg._id)
		}
	}

	const handleDeletePackage = async () => {
		if (selectedPackageIndex !== null) {
			const pkg = packages[selectedPackageIndex]
			if (pkg?._id) {
				setModal({ name: 'deletePackage', data: pkg._id })
				handleMenuClose()
			}
		}
	}

	const handleAddConstraint = () => {
		setAddonFormData({
			...addonFormData,
			constraints: [
				...addonFormData.constraints,
				{ label: '', value: '' },
			],
		})
	}

	const handleRemoveConstraint = (index) => {
		const updated = addonFormData.constraints.filter((_, i) => i !== index)
		setAddonFormData({ ...addonFormData, constraints: updated })
	}

	const handleConstraintChange = (index, field, value) => {
		const updated = [...addonFormData.constraints]
		updated[index] = { ...updated[index], [field]: value }
		setAddonFormData({ ...addonFormData, constraints: updated })
	}

	const handleDeleteAddonInPackage = async (addonOrId) => {
		setModal({ name: 'deleteAddon', data: addonOrId })
	}

	const handleAttachAddonToPackage = useCallback(
		async (packageId, selectedAddonId) => {
			try {
				const result = await dispatch(
					updatePackage({
						id: packageId,
						data: {
							addOnsIds: [
								...(packageFormData.addOnsIds || []),
								selectedAddonId,
							],
						},
					})
				).unwrap()
				if (result?.success) {
					dispatch(getPackages({ vendorId }))
					handleCloseAddonModal()
					handleClosePackageModal()
				}
			} catch (error) {
				console.error('Failed to attach addon to package:', error)
			}
		},
		[]
	)

	const handleCloseDeleteModal = () => {
		setModal({
			name: '',
			data: null,
		})
		setDeleting(false)
	}

	const confirmDeletePackage = async () => {
		setDeleting(true)
		try {
			if (!modal.data) throw new Error('Package not found')
			const result = await dispatch(deletePackage(modal.data)).unwrap()
			if (result?.success) {
				dispatch(getPackages({ vendorId }))
			}
		} catch (error) {
			console.error('Failed to delete package:', error)
		} finally {
			handleCloseDeleteModal()
		}
	}

	const confirmDeleteAddon = async () => {
		setDeleting(true)
		try {
			const { addon, index } = resolveAddonFromFormData(modal.data)
			if (!addon) throw new Error('Addon not found')
			if (addon._id) {
				const result = await dispatch(deleteAddOn(addon._id)).unwrap()

				if (result?.success) {
					const updatedAddons = (
						packageFormData.addOnsIds || []
					).filter(
						(a) =>
							!(a && typeof a === 'object' && a._id === addon._id)
					)

					setPackageFormData({
						...packageFormData,
						addOnsIds: updatedAddons,
					})

					dispatch(getPackages({ vendorId }))
				}
			}

			if (index !== null) {
				const updated = { ...packageFormData }
				updated.addOnsIds = (updated.addOnsIds || []).filter(
					(_, i) => i !== index
				)
				setPackageFormData(updated)
			}
		} catch (error) {
			console.error('Failed to delete addon from package modal:', error)
		} finally {
			handleCloseDeleteModal()
		}
	}
	return (
		<>
			<PackageModal
				isLoading={isLoading}
				open={packageModalOpen}
				onClose={handleClosePackageModal}
				isEditing={isEditingPackage}
				formData={packageFormData}
				onFormDataChange={setPackageFormData}
				onImageChange={handlePackageImageChange}
				onSave={handleSavePackage}
				packageCreated={packageCreated}
				originalData={originalPackageData}
				packageId={packageModalOpen ? editingPackageId : null}
				onAddonCreate={handleOpenAddonModal}
				onAddonEdit={handleEditAddonInPackage}
				onAddonDelete={handleDeleteAddonInPackage}
				onAddonAttach={handleAttachAddonToPackage}
			/>

			<AddOnModal
				isLoading={isLoading}
				open={addonModalOpen}
				onClose={handleCloseAddonModal}
				isEditing={isEditingAddon}
				formData={addonFormData}
				onFormDataChange={setAddonFormData}
				onImageChange={handleAddonImageChange}
				onSave={handleSaveAddon}
				packages={packages}
				onConstraintAdd={handleAddConstraint}
				onConstraintRemove={handleRemoveConstraint}
				onConstraintChange={handleConstraintChange}
			/>

			<PackagesListView
				packages={packages}
				cardStyle={cardStyle}
				vendorId={vendorId}
				showActions={true}
				onAddPackage={handleOpenPackageModal}
				onOpenMenu={handleMenuOpen}
			/>

			<Menu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor)}
				onClose={handleMenuClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				sx={{
					'& .MuiPaper-root': {
						boxShadow: '0px 0px 4px 0px #00000040',
						border: '1px solid #DFE4EB',
						borderRadius: '12px',
						minWidth: '148px',
					},
					'& .MuiList-root': {
						padding: '6px 0 !important',
					},
					'& .MuiTypography-root': {
						fontSize: '14px',
						fontWeight: '500',
						color: '#313131',
						fontFamily: 'Inter',
					},
				}}
			>
				<MenuItem onClick={handleEditPackage}>Edit</MenuItem>
				<MenuItem
					onClick={handleDeletePackage}
					sx={{ color: '#D32F2F' }}
				>
					Delete
				</MenuItem>
			</Menu>

			<CommonDeleteModal
				open={modal.name === 'deletePackage'}
				handleClose={() => {
					if (!deleting) {
						handleCloseDeleteModal()
					}
				}}
				heading="Delete Package"
				subHeading="Are you sure you want to delete this package?"
				footerContent={
					<>
						<ButtonCustom
							variant="outlined"
							onClick={handleCloseDeleteModal}
							disabled={deleting}
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="destructive"
							onClick={confirmDeletePackage}
							disabled={deleting}
						>
							{deleting ? 'Deleting...' : 'Delete'}
						</ButtonCustom>
					</>
				}
			/>

			<CommonDeleteModal
				open={modal.name === 'deleteAddon'}
				handleClose={() => {
					if (!deleting) {
						handleCloseDeleteModal()
					}
				}}
				heading="Delete Add-On"
				subHeading="Are you sure you want to delete this add-on?"
				footerContent={
					<>
						<ButtonCustom
							variant="outlined"
							onClick={handleCloseDeleteModal}
							disabled={deleting}
						>
							Cancel
						</ButtonCustom>
						<ButtonCustom
							variant="destructive"
							onClick={confirmDeleteAddon}
							disabled={deleting}
						>
							{deleting ? 'Deleting...' : 'Delete'}
						</ButtonCustom>
					</>
				}
			/>
		</>
	)
}

export default PackagesCard
